import { Grid, IconButton, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { Company } from '../../api/api-generated';
import { departmentMiddleware, departmentValidator } from './departmentsUtils';
import React, { useEffect } from 'react';
import {
  getISOStringOneYearFromToday,
  truncateISOString,
} from '../../utils/dateTime';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import {
  useDeleteDepartmentMutation,
  useFindAllCompaniesQuery,
  useFindAlldepartmentsQuery,
  useFindAllHardwareQuery,
  useFindOneDepartmentQuery,
} from '../../api/api-hooks';
import { DepartmentForm } from './departmentsTypes';
import useDepartmentsItemCreator from './useDepartmentsItemCreator';
import useDepartmentsItemUpdater from './useDepartmentsItemUpdater';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import HardwareManagementButton from '../hardware/HardwareManagementButton';

interface Props {
  selectedDepartments: Item[];
  setSelectedDepartments: React.Dispatch<React.SetStateAction<Item[]>>;
  editModalOpenFlag: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
  company: Pick<
    Company,
    'id' | 'companyName' | 'supportPurchases' | 'departments'
  > | null;
}

const addFormInitialState: DepartmentForm = {
  company: null,
  departmentName: '',
  quoteLink: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  expirationDate: truncateISOString(getISOStringOneYearFromToday()),
  lastContacted: '',
  hardware: [],
};

const DepartmentsOptionsBar = ({
  selectedDepartments,
  setSelectedDepartments,
  editModalOpenFlag,
  setEditModalOpenFlag,
  company,
}: Props) => {
  const { data: companies } = useFindAllCompaniesQuery({});
  const [deleteDepartment] = useDeleteDepartmentMutation();
  const itemCreator = useDepartmentsItemCreator();
  const itemUpdater = useDepartmentsItemUpdater();
  const { data: allDepartments } = useFindAlldepartmentsQuery({});
  const { data: allHardware } = useFindAllHardwareQuery({});
  useEffect(() => {
    addFormInitialState.company = company;
  }, [company]);
  if (
    allDepartments === undefined ||
    allHardware === undefined ||
    companies === undefined
  )
    return null;
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedDepartments}
        setSelectedItems={setSelectedDepartments}
        handlerMiddleware={departmentMiddleware}
        itemValidator={departmentValidator}
        formInstructions='Company and department name are required.'
        addFormInitialState={addFormInitialState}
        editModalOpen={editModalOpenFlag}
        setEditModalOpen={setEditModalOpenFlag}
        itemName='Department'
        childrenCreator={(addFormState: DepartmentForm, handler: any) => (
          <Grid container>
            <Grid item container xs direction='column'>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'company');
                  }}
                  value={addFormState.company}
                  options={companies}
                  getOptionLabel={(company) => company.companyName}
                  style={{ width: 300, marginTop: '.5rem' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Company'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='departmentName'
                  label='Department Name'
                  value={addFormState.departmentName}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='quoteLink'
                  label='Quote Link'
                  value={addFormState.quoteLink}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='contactName'
                  label='Contact Name'
                  value={addFormState.contactName}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='contactEmail'
                  label='Contact Email'
                  value={addFormState.contactEmail}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='contactPhone'
                  label='Contact Phone'
                  value={addFormState.contactPhone}
                  onChange={handler}
                />
              </Grid>
            </Grid>
            <Grid item container xs direction='column'>
              <Grid item style={{ width: '100%' }}>
                <Typography>Last Contacted</Typography>
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <TextField
                  type='date'
                  margin='dense'
                  name='lastContacted'
                  value={truncateISOString(
                    nullStringCheck(addFormState.lastContacted)
                  )}
                  onChange={handler}
                />
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <Typography>Expiration Date</Typography>
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <TextField
                  type='date'
                  margin='dense'
                  name='expirationDate'
                  value={truncateISOString(
                    nullStringCheck(addFormState.expirationDate)
                  )}
                  onChange={handler}
                />
              </Grid>
              <Grid container item xs direction='column' spacing={1}>
                <Grid container item spacing={1}>
                  <Grid item style={{ marginBottom: '.7rem' }}>
                    <Typography variant='subtitle1'>
                      Associated Hardware:
                    </Typography>
                  </Grid>
                  {addFormState.hardware.map((hardware, idx) => (
                    <Grid
                      item
                      container
                      spacing={1}
                      alignItems='center'
                      key={idx}
                      style={{ marginLeft: '1.5rem' }}
                    >
                      <Grid item>
                        <Autocomplete
                          onChange={(event, value) => {
                            handler(event, value, `hardware${idx}`);
                          }}
                          value={hardware}
                          options={allHardware.filter((item) =>
                            item?.companies.some(
                              (hardwareCompany) =>
                                hardwareCompany.id === addFormState.company?.id
                            )
                          )}
                          getOptionLabel={(hardware) => hardware.serial}
                          style={{ width: 300 }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Select Hardware'
                              variant='outlined'
                            />
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <IconButton
                          aria-label='Remove hardware'
                          onClick={(event) =>
                            handler(event, idx, 'deleteHardware')
                          }
                          size='large'
                        >
                          <DeleteOutline color='error' />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid item style={{ marginLeft: '3rem' }}>
                  <IconButton
                    aria-label='Add hardware'
                    onClick={(event: React.MouseEvent) => {
                      handler(event, null, 'newHardware');
                    }}
                    size='large'
                  >
                    <AddCircleOutline color='primary' />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid
                item
                container
                spacing={1}
                alignItems='center'
                style={{ marginTop: '1rem' }}
              >
                <Grid item>
                  <Typography>
                    Can't find the hardware item you want to link to this
                    record?{' '}
                  </Typography>
                </Grid>
                <Grid item>
                  <HardwareManagementButton />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={itemCreator}
        itemGetter={useFindOneDepartmentQuery}
        itemUpdater={itemUpdater}
        itemDeleter={deleteDepartment}
      />
    </>
  );
};

export default DepartmentsOptionsBar;
